






























































































































import { mapActions } from 'vuex'
import { ModuleNames } from '@/store/module-names'
import { ActionNames } from '@/store/modules/exercise/keys'

import moment from 'moment-timezone'

import { DataPointStages, VitalStatsDisplayOptions } from '@/constants/exercise-program'
import { VitalSignReading, emptyVitalSignReading } from '@/jsonapi/PatientExercise'
import { LIVE_DASHBOARD_TYPES as LiveDashboardTypes } from '@/constants/live-dashboard'

import ExerciseSessionReportVitalsGraph from '@/components/exercises/components/ExerciseSessionReportVitalsGraph.vue'

const enum SeriesForGraph {
  heartRateBpm = 'heartRateBpm',
  bloodOxygenPerc = 'bloodOxygenPerc',
}

const enum TableColumnHeaders {
  Time = 'Time',
  HeartRate = 'HR(bpm)',
  BloodOxygen = 'SpO2',
  Borg = 'BORG',
  O2Lpm = 'O2(Lpm)',
  Actions = 'Actions',
}

const enum TableColumnProps {
  Time = 'timestamp',
  HeartRate = 'heartRateBpm',
  BloodOxygen = 'bloodOxygenPerc',
  Borg = 'borgScale',
  O2Lpm = 'o2Lpm',
}

export default {
  components: {
    ExerciseSessionReportVitalsGraph,
  },
  props: {
    patientExercise: {
      type: Object,
      required: true,
    },
    vitalStatsDisplay: {
      type: String,
      default: VitalStatsDisplayOptions.Table,
    },
    isGeneratingPdf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPatchingPatientExercise: false,
      showEditVitalReadingsDialog: false,
      vitalSignReadingForEdit: 0,
      vitalSignReadings: [emptyVitalSignReading, emptyVitalSignReading, emptyVitalSignReading],
      tableColumnHeaders: [
        TableColumnHeaders.Time,
        TableColumnHeaders.HeartRate,
        TableColumnHeaders.BloodOxygen,
        TableColumnHeaders.Borg,
        TableColumnHeaders.O2Lpm,
        TableColumnHeaders.Actions,
      ],
      tableColumnProps: [
        TableColumnProps.Time,
        TableColumnProps.HeartRate,
        TableColumnProps.BloodOxygen,
        TableColumnProps.Borg,
        TableColumnProps.O2Lpm,
      ],
      timestampProp: TableColumnProps.Time,
      actionsColumnHeader: TableColumnHeaders.Actions,
      dataPointTableHeaders: [
        {
          text: 'Time',
          value: 'timestamp',
        },
        {
          text: 'HR(bpm)',
          value: 'heartRateBpm',
          isEditable: true,
        },
        {
          text: 'Sp02',
          value: 'bloodOxygenPerc',
          isEditable: true,
        },
        {
          text: 'BORG',
          value: 'borgScale',
          isEditable: true,
        },
        {
          text: 'O2(Lpm)',
          value: 'o2Lpm',
          isEditable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
        },
      ].map(header => ({
        ...header,
        sortable: false,
        divider: true,
        width: '16.6667%',
        class: ['white--text', 'teal'],
        slotName: `item.${header.value}`,
      })),
      graphYAxisLabels: ['Beginning', 'Middle', 'End'],
    }
  },
  computed: {
    displayAsTable() {
      return this.vitalStatsDisplay === VitalStatsDisplayOptions.Table
    },
    displayAsGraph() {
      return this.vitalStatsDisplay === VitalStatsDisplayOptions.Graph
    },
    heartRateBpmSeries() {
      return this.getSeriesForGraph(SeriesForGraph.heartRateBpm)
    },
    bloodOxygenPercSeries() {
      return this.getSeriesForGraph(SeriesForGraph.bloodOxygenPerc)
    },
    vitalStatsGraphs() {
      return [
        {
          key: LiveDashboardTypes.PulseRates,
          data: this.heartRateBpmSeries,
        },
        {
          key: LiveDashboardTypes.OxygenSaturations,
          data: this.bloodOxygenPercSeries,
        },
      ]
    },
    editableDataPoints() {
      return this.dataPointTableHeaders
        .filter(header => header.isEditable)
        .map(header => ({
          label: header.text,
          value: header.value,
        }))
    },
    formattedTimeForEditVitalReadingsDialog() {
      return this.vitalSignReadings[this.vitalSignReadingForEdit].timestampForTimePicker || '-'
    },
    formattedExerciseDate() {
      return moment(this.patientExercise.exerciseDate).format('Do MMMM YYYY')
    },
  },
  mounted() {
    this.setVitalSignReadings()
  },
  methods: {
    getSeriesForGraph(prop) {
      if (this.patientExercise.vitalSignReadings) {
        return this.patientExercise.vitalSignReadings.map(dp => ({
          value: dp[prop],
          timestamp: dp.timestamp,
        }))
      }
      return []
    },
    shortFormatTimestamp(timestamp) {
      return timestamp ? moment(timestamp).format('HH:mm A') : '-'
    },
    setVitalSignReadings() {
      const existingReadings = this.vitalSignReadings.map((reading, index) => {
        if (this.patientExercise.vitalSignReadings[index]) {
          return this.patientExercise.vitalSignReadings[index]
        } else {
          return reading
        }
      })
      this.vitalSignReadings = existingReadings
        .sort((a, b) => {
          if (a.timestamp === null) {
            return 1
          } else if (b.timestamp === null) {
            return -1
          } else {
            a.timestamp < b.timestamp ? 1 : -1
          }
        })
        .map((reading, index) => ({
          ...reading,
          timestampForTimePicker: reading.timestamp ? moment(reading.timestamp).format('HH:mm') : null,
          index,
        }))
    },
    setVitalSignReadingForEdit(i) {
      this.vitalSignReadingForEdit = i
    },
    closeEditVitalReadingsDialog() {
      this.showEditVitalReadingsDialog = false
      this.setVitalSignReadings()
    },
    resolveReadingTime(time) {
      if (!time) return null
      const date = moment(this.patientExercise.exerciseDate).format('YYYY-MM-DD')
      const iso = `${date}T${time}:00`
      return moment(iso).valueOf()
    },
    async saveEditedVitalReadings() {
      this.isPatchingPatientExercise = true
      const vitalSignReadings = this.vitalSignReadings.map(reading => {
        return {
          timestamp: this.resolveReadingTime(reading.timestampForTimePicker),
          heartRateBpm: reading.heartRateBpm,
          bloodOxygenPerc: reading.bloodOxygenPerc,
          borgScale: reading.borgScale,
          o2Lpm: reading.o2Lpm,
        }
      })
      await this[ActionNames.patchExercise]({
        id: this.patientExercise.id,
        attributes: {
          vitalSignReadings,
        },
      })
      await this[ActionNames.getExercises]()
      this.isPatchingPatientExercise = false
      this.showEditVitalReadingsDialog = false
      this.setVitalSignReadings()
    },
    ...mapActions(ModuleNames.exercise, [ActionNames.getExercises, ActionNames.patchExercise]),
  },
}
